function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import { useMemo } from 'react';
import { Decimal } from 'decimal.js';
import { compact } from 'lodash-es';
import { Controller, useFormContext } from 'react-hook-form';
import { TextField, Typography, WithStatusIcon } from 'seagull';
import { ServiceFormDataType } from "../BillClientModal.types";
import BillingItemNote from "../BillingNote/BillingItemNote";
import { ServiceEffectiveDateCallout, ServicePendingAmendmentCallout } from "../Callouts/Callouts";
import CumulativeWarningCallout from "../Callouts/CumulativeWarningCallout";
import { serviceCostModel } from "../../../models";
import { BillingStageType } from "../../../models/manual-service-billing";
import { utils } from "../../../utils";
import pluralize from "../../../utils/pluralize";
import { connectControllerToTextField } from "../../../utils/react-hook-form.utils";
import styles from "../BillClientModal.module.scss";
import BillTableItem from "../BillTableItem/BillTableItem";
import { calculateTotalFormQuantity, getQuantityWarning, getTableDefs, getTotal, qtyWarningToText } from "./BillServiceTableItem.utils";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const maxQuantity = new Decimal(999999);
const maxPrice = new Decimal(999999);
const maxError = 'max';
const requiredError = 'required';
export default function BillServiceTableItem(props) {
  const {
    control,
    watch
  } = useFormContext();
  const [items, issueDate, mode] = watch(['items', 'issueDate', 'mode']);
  const {
    manualServiceBilling,
    actionColumn,
    itemIndex,
    serviceItem,
    relationshipEffectiveDate
  } = props;
  const {
    price: priceInput,
    quantity: quantityInput,
    cost: costInput
  } = serviceItem.service;
  const service = serviceItem.service;
  const fullService = serviceItem.fullService;
  const cost = useMemo(() => {
    if (service.type === ServiceFormDataType.Stages) {
      const specificStage = service.billingStages.find(stage => stage.stageNumber === service.stageNumber);
      if (specificStage) return _objectSpread(_objectSpread({}, costInput), {}, {
        price: specificStage.amount
      });
      return _objectSpread(_objectSpread({}, costInput), {}, {
        price: service.billingStages.filter(stage => stage.type === BillingStageType.Available)[0].amount
      });
    }
    return costInput;
  }, [service, costInput]);
  const quantity = useMemo(() => quantityInput ? new Decimal(+quantityInput) : new Decimal(0), [quantityInput]);
  const total = useMemo(() => getTotal(fullService, service, quantity, priceInput), [quantity, priceInput, fullService, service]);
  const {
    columnDefs,
    headerItems
  } = useMemo(() => getTableDefs(fullService), [fullService]);
  const totalServiceQuantity = calculateTotalFormQuantity(items, fullService.id);
  const quantityWarning = getQuantityWarning(fullService, totalServiceQuantity, issueDate, manualServiceBilling);
  const isVariablePrice = serviceCostModel.isVariablePrice(cost);
  const isRangePrice = serviceCostModel.isRangePrice(cost);
  const getRangeController = cost => /*#__PURE__*/_jsx(Controller, {
    control: control,
    name: "items.".concat(itemIndex, ".service.price"),
    rules: {
      validate: value => {
        if (!value) return requiredError;
        const asNumber = new Decimal(+value);
        if (asNumber.greaterThan(maxPrice)) return maxError;
      }
    },
    render: renderProps => {
      const {
        field,
        fieldState: {
          error
        }
      } = renderProps;
      const isWarning = field.value !== '' && cost.maxPrice.lessThan(field.value);
      return /*#__PURE__*/_jsx(TextField, _objectSpread(_objectSpread({
        collapseHelperText: false,
        isMoney: true
      }, connectControllerToTextField(renderProps, {
        noHelperText: true,
        alwaysShowErrors: true
      })), {}, {
        width: 130,
        inputProps: {
          min: 0,
          className: styles.amountCell
        },
        testid: "bill-service-".concat(itemIndex, "-table-rate"),
        warning: isWarning && !error,
        suffix: (error || isWarning) && /*#__PURE__*/_jsx(WithStatusIcon, {
          "data-testid": "bill-service-table-rate-warning-message",
          info: (error === null || error === void 0 ? void 0 : error.message) === maxError ? "Can't input more than ".concat(utils.formatDollarAmount(maxPrice)) : isWarning ? "Payment above the maximum of ".concat(utils.formatDollarAmount(cost.maxPrice), " will require your client's approval") : 'Please enter a positive number',
          statusType: error ? 'error' : 'warning',
          size: "medium"
        })
      }));
    }
  });
  const rowItems = compact([{
    value: /*#__PURE__*/_jsx(Typography, {
      ellipsis: true,
      "data-testid": "bill-service-".concat(itemIndex, "-table-header-name"),
      children: fullService.name
    })
  }, {
    value: /*#__PURE__*/_jsx("div", {
      className: styles.quantityCell,
      children: isVariablePrice && /*#__PURE__*/_jsx(Controller, {
        control: control,
        name: "items.".concat(itemIndex, ".service.quantity"),
        rules: {
          validate: quantity => {
            if (!quantity) return requiredError;
            const asNumber = new Decimal(+quantity);
            if (asNumber.lessThanOrEqualTo(0)) return requiredError;
            if (asNumber.greaterThan(maxQuantity)) return maxError;
          }
        },
        render: renderProps => {
          const {
            fieldState: {
              error
            }
          } = renderProps;
          const isWarning = !!quantityWarning && !error;
          return /*#__PURE__*/_jsx(TextField, _objectSpread(_objectSpread({
            collapseHelperText: false,
            isMoney: true,
            size: "small",
            width: 130,
            inputProps: {
              min: 0,
              className: styles.amountCell
            }
          }, connectControllerToTextField(renderProps, {
            noHelperText: true,
            alwaysShowErrors: true
          })), {}, {
            testid: "bill-service-".concat(itemIndex, "-table-quantity"),
            warning: isWarning,
            suffix: (error || !!quantityWarning) && /*#__PURE__*/_jsx(WithStatusIcon, {
              "data-testid": "bill-service-".concat(itemIndex, "-table-quantity-warning-message"),
              "data-intent": error ? maxError : quantityWarning || '',
              info: (error === null || error === void 0 ? void 0 : error.message) === maxError ? "Can't input more than ".concat(utils.formatNumberWithCommas(maxQuantity), " ").concat(pluralize(serviceCostModel.formattedUnitName(cost), maxQuantity.toNumber())) : quantityWarning ? qtyWarningToText[quantityWarning] : 'Please enter a positive number',
              statusType: isWarning ? 'warning' : 'error',
              size: "medium"
            })
          }));
        }
      })
    })
  }, {
    value: isRangePrice ? getRangeController(cost) : /*#__PURE__*/_jsxs("div", {
      className: styles.rateWithUnit,
      children: [/*#__PURE__*/_jsx(Typography, {
        "data-testid": "bill-service-table-rate-display",
        children: utils.formatDollarAmount(cost.price)
      }), isVariablePrice && /*#__PURE__*/_jsxs(Typography, {
        ellipsis: true,
        variant: "paragraph2",
        skin: "secondary",
        children: ["/", serviceCostModel.formattedUnitName(cost)]
      })]
    })
  }, {
    value: cost.discount ? /*#__PURE__*/_jsx(Typography, {
      align: "right",
      children: cost.discount.format()
    }) : null
  }, isVariablePrice ? {
    value: cost.unitCap ? /*#__PURE__*/_jsx(Typography, {
      align: "right",
      "data-testid": "bill-service-table-preapproved-units",
      children: utils.formatNumberWithCommas(cost.unitCap)
    }) : null
  } : isRangePrice ? {
    value: /*#__PURE__*/_jsx(Typography, {
      align: "right",
      "data-testid": "bill-service-table-rate-range",
      children: utils.getPriceRangeText(cost)
    })
  } : {
    value: null
  }, {
    value: /*#__PURE__*/_jsx(Typography, {
      align: "right",
      "data-testid": "bill-service-table-total",
      children: utils.formatDollarAmount(total)
    })
  }]);
  return /*#__PURE__*/_jsx(BillTableItem, {
    columnDefs: columnDefs,
    headerItems: headerItems,
    rowItems: rowItems,
    headerTestId: "bill-service-table-header",
    actionColumn: actionColumn,
    footer: /*#__PURE__*/_jsxs("div", {
      className: styles.noteAndCalloutsContainer,
      children: [/*#__PURE__*/_jsx(BillingItemNote, {
        testid: "manual-bill-note-".concat(itemIndex),
        name: "items.".concat(itemIndex, ".note")
      }), /*#__PURE__*/_jsx(CumulativeWarningCallout, {
        service: fullService,
        issueDate: issueDate,
        manualServiceBilling: manualServiceBilling,
        testid: "bill-service-cumulative-".concat(itemIndex)
      }), /*#__PURE__*/_jsx(ServicePendingAmendmentCallout, {
        service: fullService,
        testid: "pending-amendment-callout-".concat(itemIndex)
      }), /*#__PURE__*/_jsx(ServiceEffectiveDateCallout, {
        mode: mode,
        service: fullService,
        issueDate: issueDate,
        relationshipEffectiveDate: relationshipEffectiveDate,
        testid: "effective-date-callout-".concat(itemIndex)
      })]
    })
  });
}